// =============================================================================
// BATCH WIZARD CONTEXT v2
// Bridges the DA Agent chat panel with the batch creation wizard.
// =============================================================================
// Enhancements in v2:
//   - Per-screen state schemas
//   - Action queue processor with sequential execution
//   - Typing animations, field highlighting, button click simulation
//   - Error state management with retry support
//   - Full control over all 6 wizard steps
// =============================================================================

import React, {
  createContext,
  useState,
  useCallback,
  useRef,
  useContext,
} from 'react';

// ---------------------------------------------------------------------------
// Per-screen state schemas (used for validation and initialization)
// ---------------------------------------------------------------------------
export const SCREEN_STATES = {
  0: {
    // Bundle Selection
    selectedBundle: null,
    bundles: [],
    userServices: [],
  },
  1: {
    // Inbound Connector / Source Selection
    selectedConnectorId: null,
    selectedSourceId: null,
    connectorName: '',
    connectors: [],
    sources: [],
  },
  2: {
    // File Selection & Config (Configure Source)
    fileName: '',
    filePath: '',
    delimiter: ',',
    description: '',
    rejectThreshold: 20,
    fileProcessed: false,
    searchResults: [],
    bucketName: '',
  },
  3: {
    // Schema Configuration
    schemaFields: [],
    nullableFields: [],
    encryptedFields: [],
    confirmed: false,
  },
  4: {
    // Advanced Configuration (Mapping)
    mappings: {},
    fieldAdjustments: [],
    dedupeKey: null,
    orderHandling: null,
    generatedFields: [],
    rejectRules: [],
  },
  5: {
    // Review & Create / Outbound
    outboundConnectorId: null,
    outboundConnectorName: '',
    summary: {},
    readyToCreate: false,
  },
};

// ---------------------------------------------------------------------------
// Timing utilities
// ---------------------------------------------------------------------------
const sleep = ms => new Promise(resolve => setTimeout(resolve, ms));

/**
 * Poll a condition function until it returns true, or timeout.
 * Resolves true if condition met, false if timed out.
 */
const waitForUiUpdate = async (
  conditionFn,
  timeoutMs = 5000,
  intervalMs = 200
) => {
  const start = Date.now();
  while (Date.now() - start < timeoutMs) {
    try {
      if (conditionFn()) return true;
    } catch {
      // condition threw — keep polling
    }
    await sleep(intervalMs);
  }
  console.warn(
    '[BatchWizardContext] waitForUiUpdate timed out after',
    timeoutMs,
    'ms'
  );
  return false;
};

// ---------------------------------------------------------------------------
// Context Definition
// ---------------------------------------------------------------------------
export const BatchWizardContext = createContext({
  isActive: false,
  setIsActive: () => {},
  currentStep: -1,
  setCurrentStep: () => {},
  wizardState: {},
  setWizardState: () => {},
  navigate: () => {},
  goToStep: () => {},
  registerHistory: () => {},
  registerWizardControls: () => {},
  executeAction: () => {},
  isPanelOpen: true,
  setIsPanelOpen: () => {},
  activeAutoAction: null,
  setActiveAutoAction: () => {},
  errorState: null,
  setErrorState: () => {},
  highlightedField: null,
  setHighlightedField: () => {},
  resetContext: () => {},
});

// ---------------------------------------------------------------------------
// Provider Component
// ---------------------------------------------------------------------------
export const BatchWizardProvider = ({ children }) => {
  const [isActive, setIsActive] = useState(false);
  const [currentStep, setCurrentStep] = useState(-1);
  const [wizardState, setWizardState] = useState({});
  const [isPanelOpen, setIsPanelOpen] = useState(true);
  const [activeAutoAction, setActiveAutoAction] = useState(null);
  const [errorState, setErrorState] = useState(null);
  const [highlightedField, setHighlightedField] = useState(null);

  const historyRef = useRef(null);
  const wizardControlsRef = useRef({});
  const actionQueueRef = useRef([]);
  const isProcessingRef = useRef(false);
  const pendingStep2FileRef = useRef(null);

  /**
   * Register React Router history for navigation.
   */
  const registerHistory = useCallback(history => {
    historyRef.current = history;
  }, []);

  /**
   * Register controls from the wizard page so the chat can manipulate UI.
   */
  const registerWizardControls = useCallback(controls => {
    console.log(
      '[BatchWizardContext] Registering wizard controls:',
      Object.keys(controls)
    );
    wizardControlsRef.current = controls;
  }, []);

  /**
   * Navigate to a route using registered React Router history.
   */
  const navigate = useCallback(route => {
    if (historyRef.current && route) {
      console.log('[BatchWizardContext] Navigating to:', route);
      historyRef.current.push(route);
    } else {
      console.warn(
        '[BatchWizardContext] Cannot navigate - history not registered or no route'
      );
    }
  }, []);

  /**
   * Go to a specific step and optionally navigate.
   */
  const goToStep = useCallback(step => {
    console.log('[BatchWizardContext] goToStep:', step);
    const controls = wizardControlsRef.current;
    if (controls?.goToStep) {
      controls.goToStep(step);
    }
    setCurrentStep(step);
  }, []);

  /**
   * Reset all context state - used when starting a new chat.
   * Also resets the wizard page-level state (form fields, activeStep)
   * via the registered controls.
   */
  const resetContext = useCallback(() => {
    console.log('[BatchWizardContext] Resetting context...');
    setIsActive(false);
    setCurrentStep(-1);
    setWizardState({});
    setActiveAutoAction(null);
    setErrorState(null);
    setHighlightedField(null);
    actionQueueRef.current = [];
    isProcessingRef.current = false;

    // Reset wizard page-level state if controls are registered
    const controls = wizardControlsRef.current;
    if (controls?.resetWizardPage) {
      console.log('[BatchWizardContext] Resetting wizard page state...');
      controls.resetWizardPage();
    }
  }, []);

  // Keep a ref that always points to the latest executeActionInternal
  // so processActionQueue never uses a stale closure.
  const executeActionRef = useRef(null);

  /**
   * Process action queue sequentially.
   */
  const processActionQueue = useCallback(async () => {
    if (isProcessingRef.current) return;
    isProcessingRef.current = true;

    while (actionQueueRef.current.length > 0) {
      const action = actionQueueRef.current.shift();
      setActiveAutoAction(action);

      // Small delay between actions for visual effect
      await sleep(150);

      // Always call the LATEST version of executeActionInternal via ref
      if (executeActionRef.current) {
        await executeActionRef.current(action);
      }

      // Clear highlight after action
      await sleep(300);
    }

    setActiveAutoAction(null);
    isProcessingRef.current = false;
  }, []);

  /**
   * Internal action executor - handles all action types for all screens.
   */
  const executeActionInternal = useCallback(
    async action => {
      console.log('[BatchWizardContext] executeActionInternal:', action);
      if (!action) return;

      const controls = wizardControlsRef.current;
      console.log(
        '[BatchWizardContext] Available controls:',
        Object.keys(controls || {})
      );

      const fn = action.function;
      const params = action.params || {};
      const switchKey = fn || action.type || action.action;
      console.log(
        '[BatchWizardContext] Switch key:',
        switchKey,
        'params:',
        params
      );

      switch (switchKey) {
        // =====================================================================
        // NAVIGATION ACTIONS
        // =====================================================================
        case 'navigate_to':
        case 'navigate':
          if (params.route || action.route) {
            navigate(params.route || action.route);
            const step = params.step ?? action.step;
            if (typeof step === 'number' && step >= 0) {
              setIsActive(true);
              setCurrentStep(step);
            }
          }
          break;

        case 'navigate_to_batch_list':
          navigate('/dqh/batch-processes');
          break;

        case 'navigate_to_create_process':
          navigate('/dqh/create-process');
          setIsActive(true);
          setCurrentStep(0);
          break;

        case 'go_to_step':
        case 'next_step':
          if (typeof params.step === 'number') {
            goToStep(params.step);
          } else if (switchKey === 'next_step') {
            goToStep(currentStep + 1);
          }
          break;

        case 'previous_step':
          goToStep(Math.max(0, currentStep - 1));
          break;

        // =====================================================================
        // STEP 0: BUNDLE SELECTION
        // =====================================================================
        case 'show_bundles':
          // Options shown in chat, no UI action needed
          break;

        case 'select':
        case 'select_bundle':
          console.log('[BatchWizardContext] select_bundle case hit');
          if (controls?.selectBundle) {
            const bundleId = params.bundleId || params.bundle_id || params.id;
            const bundleName =
              params.bundleName || params.bundle_name || params.name;
            console.log('[BatchWizardContext] Calling selectBundle with:', {
              bundleId,
              bundleName,
            });
            controls.selectBundle(bundleId, bundleName, params.bundle);
            // Wait until the detail panel actually renders in the DOM.
            const bundleVisible = await waitForUiUpdate(
              () =>
                !!document.querySelector('[data-agent-id="bundle-selected"]'),
              12000,
              250
            );
            if (!bundleVisible) {
              console.warn(
                '[BatchWizardContext] bundle selection did not visually reflect in time'
              );
            }
            // Do NOT auto-advance — let the user review the bundle info
            // and click Continue or tell the agent to proceed.
          } else {
            console.warn(
              '[BatchWizardContext] selectBundle control not registered'
            );
          }
          break;

        // =====================================================================
        // STEP 1: SOURCE/CONNECTOR SELECTION
        // =====================================================================
        case 'show_connectors':
        case 'show_sources':
          // Options shown in chat, no UI action needed
          break;

        case 'select_connector':
        case 'select_source':
          console.log('[BatchWizardContext] select_source/connector case hit');
          if (controls?.selectSource) {
            const sourceId =
              params.connectorId ||
              params.connector_id ||
              params.sourceId ||
              params.source_id ||
              params.id;
            const sourceName =
              params.connectorName ||
              params.connector_name ||
              params.sourceName ||
              params.source_name ||
              params.name;
            console.log('[BatchWizardContext] Calling selectSource with:', {
              sourceId,
              sourceName,
            });
            await controls.selectSource(sourceId, sourceName);
            // Wait for the selected connector tile to appear in the DOM
            // with the data-agent-selected attribute — proves the tile
            // rendered AND shows the selection indicator (check icon, border).
            const sourceVisible = await waitForUiUpdate(
              () => !!document.querySelector('[data-agent-selected="true"]'),
              10000,
              250
            );
            if (!sourceVisible) {
              console.warn(
                '[BatchWizardContext] source selection did not visually reflect in time'
              );
            }
            // Generous pause so the user can SEE the highlighted tile
            await sleep(2500);
            console.log(
              '[BatchWizardContext] Advancing to step 2 after source selection'
            );
            if (controls?.nextStep) {
              controls.nextStep();
            }
          } else {
            console.warn(
              '[BatchWizardContext] selectSource control not registered'
            );
          }
          break;

        // =====================================================================
        // STEP 2: FILE SELECTION & CONFIGURATION
        // =====================================================================
        case 'search_files':
        case 'show_search_results':
        case 'show_file_results':
          // Search done server-side, results shown in chat
          break;

        case 'select_file':
          console.log('[BatchWizardContext] select_file case hit');
          if (controls?.selectFile) {
            const fileName = params.fileName || params.file_name || params.name;
            const filePath = params.filePath || params.file_path || params.path;
            const fileBucket = params.bucket || params.bucket_name;
            console.log('[BatchWizardContext] Calling selectFile with:', {
              fileName,
              filePath,
              fileBucket,
            });
            pendingStep2FileRef.current = {
              fileName: fileName || '',
              filePath: filePath || '',
            };
            controls.selectFile(fileName, filePath, fileBucket);
          } else {
            console.warn(
              '[BatchWizardContext] selectFile control not registered'
            );
          }
          break;

        case 'set_delimiter':
          console.log('[BatchWizardContext] set_delimiter case hit');
          if (controls?.setFieldValue) {
            const delimiter = params.delimiter || params.value;
            controls.setFieldValue('delimiter', delimiter);
          }
          break;

        case 'set_escape':
          console.log('[BatchWizardContext] set_escape case hit');
          if (controls?.setFieldValue) {
            const escapeValue =
              params.escape || params.escape_char || params.value;
            controls.setFieldValue('escape', escapeValue);
          }
          break;

        case 'set_description':
        case 'set_feed_description':
          if (controls?.setFieldValue) {
            controls.setFieldValue(
              'description',
              params.description || params.value
            );
          }
          break;

        case 'set_reject_threshold':
          if (controls?.setFieldValue) {
            const raw = params.threshold ?? params.value ?? 20;
            const parsed =
              typeof raw === 'string'
                ? parseInt(raw.replace('%', '').trim(), 10)
                : raw;
            controls.setFieldValue(
              'rejectThreshold',
              Number.isFinite(parsed) ? parsed : 20
            );
          }
          break;

        case 'process_file':
        case 'click_process_file':
          console.log('[BatchWizardContext] process_file case hit');
          if (controls?.processFile) {
            // Wait until step-2 UI has both:
            // 1) process button rendered, and
            // 2) selected file reflected in wizard state.
            const ready = await waitForUiUpdate(
              () => {
                const hasProcessBtn = !!document.querySelector(
                  '[data-agent-id="process_file"]'
                );
                if (!hasProcessBtn) return false;

                const state = controls?.getWizardState?.() || {};
                const metadata = state?.ingestionMetadata || {};
                const selectedName = (
                  metadata.uploadedFileName || ''
                ).toLowerCase();
                const selectedObject = (
                  metadata.data_source_object || ''
                ).toLowerCase();
                const expected = pendingStep2FileRef.current;
                if (!expected) return selectedName.length > 0;

                const expectedName = (expected.fileName || '').toLowerCase();
                const expectedPath = (expected.filePath || '')
                  .replaceAll('\\', '/')
                  .toLowerCase();
                const expectedPathName = expectedPath.split('/').pop() || '';

                // UI considered ready when filename matches expected basename and
                // data_source_object has been derived.
                const nameMatches = expectedName
                  ? selectedName === expectedName ||
                    selectedName === expectedPathName
                  : selectedName.length > 0;
                return nameMatches && selectedObject.length > 0;
              },
              10000,
              200
            );
            if (!ready) {
              console.warn(
                '[BatchWizardContext] process_file not ready - file state not hydrated'
              );
              break;
            }

            const processBtn = document.querySelector(
              '[data-agent-id="process_file"]'
            );
            processBtn?.classList.add('da-agent-clicking');
            setTimeout(
              () => processBtn?.classList.remove('da-agent-clicking'),
              600
            );
            await sleep(300);
            controls.processFile();

            // Wait for file processing to complete (API call finishes,
            // dataProcessedStatus becomes true in the wizard state).
            const processed = await waitForUiUpdate(
              () => {
                const state = controls?.getWizardState?.() || {};
                return !!state.dataProcessedStatus;
              },
              30000,
              500
            );
            if (processed) {
              console.log('[BatchWizardContext] File processing complete');
            } else {
              console.warn(
                '[BatchWizardContext] File processing did not complete in time'
              );
            }
            pendingStep2FileRef.current = null;
          } else {
            console.warn(
              '[BatchWizardContext] processFile control not registered'
            );
          }
          break;

        case 'update_field':
        case 'set_field_value':
          if (controls?.setFieldValue) {
            const field = params.field || params.fieldName || params.field_name;
            const value = params.value;
            console.log(
              '[BatchWizardContext] Setting field:',
              field,
              '=',
              value
            );
            controls.setFieldValue(field, value);
          }
          break;

        case 'auto_fill':
          // Typing animation for a field
          if (controls?.setFieldValue) {
            const field = params.field || params.fieldName;
            const value = params.value || '';
            // Simulate typing character by character
            for (let i = 1; i <= value.length; i++) {
              controls.setFieldValue(field, value.substring(0, i));
              await sleep(30);
            }
          }
          break;

        // =====================================================================
        // STEP 3: SCHEMA CONFIGURATION
        // =====================================================================
        case 'show_schema':
        case 'display_schema':
          // Schema displayed in UI table
          break;

        case 'mark_nullable':
        case 'set_nullable':
          if (controls?.setNullable) {
            const fieldName = params.field || params.fieldName || params.column;
            const isNullable = params.nullable !== false;
            controls.setNullable(fieldName, isNullable);
          }
          break;

        case 'mark_encrypted':
        case 'set_encrypted':
          if (controls?.setEncrypted) {
            const fieldName = params.field || params.fieldName || params.column;
            const isEncrypted = params.encrypted !== false;
            controls.setEncrypted(fieldName, isEncrypted);
          }
          break;

        case 'confirm_schema':
          if (controls?.confirmSchema) {
            controls.confirmSchema();
          }
          break;

        case 'update_schema_field':
          if (controls?.updateSchemaField) {
            controls.updateSchemaField(params.field, params.updates);
          }
          break;

        // =====================================================================
        // STEP 4: ADVANCED CONFIGURATION (MAPPING)
        // =====================================================================
        case 'show_mappings':
        case 'display_mappings':
          // Mappings displayed in UI table
          break;

        case 'generate_mappings':
        case 'trigger_ai_mapping':
          console.log('[BatchWizardContext] trigger_ai_mapping case hit');
          if (controls?.triggerAiMapping) {
            // Show loading indicator
            const mapBtn = document.querySelector(
              '[data-agent-id="generate_mapping"]'
            );
            if (mapBtn) {
              mapBtn.classList.add('da-agent-clicking');
              setTimeout(
                () => mapBtn.classList.remove('da-agent-clicking'),
                600
              );
            }
            await sleep(200);
            controls.triggerAiMapping();
          } else {
            console.warn(
              '[BatchWizardContext] triggerAiMapping control not registered'
            );
          }
          break;

        case 'update_mapping':
        case 'set_mapping':
          if (controls?.updateMapping) {
            const inputField =
              params.inputField || params.input_field || params.source;
            const outputField =
              params.outputField || params.output_field || params.target;
            controls.updateMapping(inputField, outputField);
          }
          break;

        case 'set_dedupe_key':
        case 'update_dedupe':
          if (controls?.setDedupeKey) {
            controls.setDedupeKey(
              params.key || params.dedupeKey || params.dedupe_key
            );
          }
          break;

        case 'set_order_handling':
          if (controls?.setOrderHandling) {
            controls.setOrderHandling(
              params.order || params.orderHandling || params.order_handling
            );
          }
          break;

        case 'show_field_adjustments':
          // Field adjustments shown in chat message
          break;

        // =====================================================================
        // STEP 5: REVIEW & CREATE / OUTBOUND
        // =====================================================================
        case 'show_outbound_connectors':
          // Options shown in chat
          break;

        case 'select_outbound':
        case 'select_outbound_connector':
          if (controls?.selectOutbound) {
            const connectorId =
              params.connectorId || params.connector_id || params.id;
            const connectorName =
              params.connectorName || params.connector_name || params.name;
            controls.selectOutbound(connectorId, connectorName);
          }
          break;

        case 'show_summary':
        case 'display_summary':
          // Summary displayed in UI
          break;

        case 'create_batch':
        case 'finalize':
        case 'save_configuration':
          console.log('[BatchWizardContext] create_batch/finalize case hit');
          if (controls?.saveConfiguration) {
            const saveBtn = document.querySelector(
              '[data-agent-id="save_config"]'
            );
            if (saveBtn) {
              saveBtn.classList.add('da-agent-clicking');
              setTimeout(
                () => saveBtn.classList.remove('da-agent-clicking'),
                600
              );
            }
            await sleep(200);
            controls.saveConfiguration();
          } else {
            console.warn(
              '[BatchWizardContext] saveConfiguration control not registered'
            );
          }
          break;

        // =====================================================================
        // UI FEEDBACK ACTIONS
        // =====================================================================
        case 'highlight':
        case 'highlight_field': {
          const fieldToHighlight = params.field || params.fieldName;
          if (fieldToHighlight) {
            setHighlightedField(fieldToHighlight);
            // Auto-clear after 3 seconds
            setTimeout(() => setHighlightedField(null), 3000);
          }
          break;
        }

        case 'click_button': {
          const buttonId = params.buttonId || params.button_id || params.id;
          const btn = document.querySelector(`[data-agent-id="${buttonId}"]`);
          if (btn) {
            btn.classList.add('da-agent-clicking');
            await sleep(150);
            btn.click();
            setTimeout(() => btn.classList.remove('da-agent-clicking'), 450);
          }
          break;
        }

        case 'show_error':
          setErrorState({
            message: params.message || params.error || 'An error occurred',
            allowRetry: params.allowRetry || params.allow_retry || false,
          });
          break;

        case 'clear_error':
          setErrorState(null);
          break;

        case 'show_loader':
        case 'show_loading':
          // Handled by activeAutoAction state
          break;

        case 'hide_loader':
        case 'hide_loading':
          setActiveAutoAction(null);
          break;

        // =====================================================================
        // INFO/NO-OP ACTIONS
        // =====================================================================
        case 'info':
        case 'none':
          // No UI action, just display message
          break;

        default:
          console.warn(
            '[BatchWizardContext] Unknown action:',
            switchKey,
            action
          );
          break;
      }
    },
    [
      navigate,
      goToStep,
      setIsActive,
      setCurrentStep,
      setHighlightedField,
      setErrorState,
      currentStep,
    ]
  );

  // Keep the ref in sync so processActionQueue always uses the latest version
  executeActionRef.current = executeActionInternal;

  /**
   * Execute a structured action returned by the LLM.
   * Adds to action queue and processes sequentially.
   */
  const executeAction = useCallback(
    action => {
      console.log('[BatchWizardContext] executeAction called with:', action);
      if (!action) {
        console.log('[BatchWizardContext] Skipping action - null');
        return;
      }
      // Skip pure info actions
      const actionType = action.type || action.action;
      const actionFunction = action.function || action.action;
      if (actionType === 'info' && actionFunction === 'none') {
        console.log('[BatchWizardContext] Skipping pure info action');
        return;
      }

      actionQueueRef.current.push(action);
      processActionQueue();
    },
    [processActionQueue]
  );

  const value = {
    isActive,
    setIsActive,
    currentStep,
    setCurrentStep,
    wizardState,
    setWizardState,
    navigate,
    goToStep,
    registerHistory,
    registerWizardControls,
    executeAction,
    isPanelOpen,
    setIsPanelOpen,
    activeAutoAction,
    setActiveAutoAction,
    errorState,
    setErrorState,
    highlightedField,
    setHighlightedField,
    resetContext,
  };

  return (
    <BatchWizardContext.Provider value={value}>
      {children}
    </BatchWizardContext.Provider>
  );
};

/** Convenience hook */
export const useBatchWizard = () => useContext(BatchWizardContext);

export default BatchWizardContext;
